import type { ReactNode } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { PageHeader } from '@/widgets/layout/ui/AppLayout'
import { Button } from '@/shared/ui'
import {
  useAdminCompany,
  useAdminImpersonate,
  useAdminRenameCompany,
  useAdminSetCompanyBlocked,
  useAdminSetCompanyIntegrations,
} from '@/entities/admin'
import { ApiError } from '@/shared/api/client'
import type { AdminCompanyDetails } from '@/shared/api/types'
import { ElevenLabsCost } from '../shared/ElevenLabsCost'
import { AdminRemoteState } from '../shared/AdminRemoteState'
import { formatApproxUsd } from '../shared/adminCosts'
import {
  formatAdminDate,
  formatDecimal,
  formatDurationSeconds,
  formatNumber,
  formatPercent,
} from '../shared/format'
import { CompanyAuditTab, CompanyCallsTab } from './CompanyDataTabs'
import { CompanyMinutesTab } from './CompanyMinutesTab'
import { CompanyCampaignsTab, CompanyUsersTab, CompanyVoiceTab } from './CompanyOperationsTabs'
import styles from '../AdminPages.module.css'

type CompanyTab = 'overview' | 'minutes' | 'calls' | 'campaigns' | 'users' | 'voice' | 'audit'

const TABS: { value: CompanyTab; label: string }[] = [
  { value: 'overview', label: 'Обзор' },
  { value: 'minutes', label: 'Минуты' },
  { value: 'calls', label: 'Звонки' },
  { value: 'campaigns', label: 'Обзвоны' },
  { value: 'users', label: 'Пользователи' },
  { value: 'voice', label: 'Настройки голоса' },
  { value: 'audit', label: 'Журнал' },
]

export function AdminCompanyPage() {
  const { id } = useParams<{ id: string }>()
  const parsedId = Number(id)
  const companyId = Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null
  const company = useAdminCompany(companyId)
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedTab = searchParams.get('tab')
  const tab = TABS.some((item) => item.value === requestedTab)
    ? (requestedTab as CompanyTab)
    : 'overview'

  if (companyId === null) {
    return (
      <>
        <PageHeader title="Компания не найдена" />
        <AdminRemoteState kind="empty" message="Проверьте номер компании в адресе." />
      </>
    )
  }

  if (company.isPending) {
    return (
      <>
        <PageHeader title="Компания" />
        <AdminRemoteState kind="loading" />
      </>
    )
  }

  if (company.isError || !company.data) {
    const missing = company.error instanceof ApiError && company.error.status === 404
    return (
      <>
        <PageHeader title={missing ? 'Компания не найдена' : 'Компания недоступна'} />
        <AdminRemoteState
          kind={missing ? 'empty' : 'error'}
          message={missing ? 'Проверьте номер компании в адресе.' : 'Не удалось загрузить данные компании.'}
          onRetry={missing ? undefined : () => void company.refetch()}
        />
      </>
    )
  }

  return (
    <CompanyContent
      companyId={companyId}
      data={company.data}
      tab={tab}
      onTab={(next) => setSearchParams(next === 'overview' ? {} : { tab: next })}
    />
  )
}

interface CompanyContentProps {
  companyId: number
  data: AdminCompanyDetails
  tab: CompanyTab
  onTab: (tab: CompanyTab) => void
}

function CompanyContent({ companyId, data, tab, onTab }: CompanyContentProps) {
  const rename = useAdminRenameCompany(companyId)
  const access = useAdminSetCompanyBlocked(companyId)
  const integ = useAdminSetCompanyIntegrations(companyId)
  const integOn = Boolean(data.company.integrations)
  const impersonate = useAdminImpersonate()
  const primaryUser = data.users.find((user) => user.email === data.ownerEmail) ?? data.users[0]

  function renameCompany() {
    const next = window.prompt('Введите новое название компании.', data.company.name)?.trim()
    if (!next || next === data.company.name) return
    if (!window.confirm(`Переименовать компанию «${data.company.name}» в «${next}»?`)) return
    rename.mutate(next)
  }

  function changeAccess() {
    const blocked = !data.company.blockedAt
    const warning = blocked
      ? `Заблокировать компанию «${data.company.name}»? Пользователи не смогут работать в кабинете до разблокировки.`
      : `Разблокировать компанию «${data.company.name}» и вернуть пользователям доступ к кабинету?`
    if (window.confirm(warning)) access.mutate(blocked)
  }

  function changeIntegrations() {
    const warning = integOn
      ? `Выключить интеграции у «${data.company.name}»? Раздел пропадёт из кабинета, отправка в CRM и данные для бота остановятся. Настройки сохранятся.`
      : `Включить интеграции у «${data.company.name}»? В кабинете появится раздел «Интеграции».`
    if (window.confirm(warning)) integ.mutate(!integOn)
  }

  function enterCompany() {
    if (!primaryUser) return
    const confirmed = window.confirm(
      `Войти в кабинет компании «${data.company.name}» как ${primaryUser.email}? Все действия будут выполняться от имени клиента.`,
    )
    if (confirmed) impersonate.mutate(primaryUser.id)
  }

  return (
    <>
      <Link className={styles.backLink} to="/admin/companies">
        ← Ко всем компаниям
      </Link>
      <div className={styles.headerRow}>
        <PageHeader title={data.company.name} description={`Владелец: ${data.ownerEmail}`} />
        <div className={styles.headerActions}>
          <span
            className={`${styles.status} ${data.company.blockedAt ? styles.statusDanger : styles.statusGood}`}
          >
            {data.company.blockedAt ? 'Заблокирована' : 'Работает'}
          </span>
          <Button variant="secondary" size="sm" onClick={renameCompany} loading={rename.isPending}>
            Переименовать
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={changeIntegrations}
            loading={integ.isPending}
            title="Раздел «Интеграции» в кабинете клиента: отправка в CRM, данные для бота, приём заявок."
          >
            {integOn ? 'Интеграции: вкл' : 'Интеграции: выкл'}
          </Button>
          <Button
            variant={data.company.blockedAt ? 'secondary' : 'danger'}
            size="sm"
            onClick={changeAccess}
            loading={access.isPending}
          >
            {data.company.blockedAt ? 'Разблокировать' : 'Заблокировать'}
          </Button>
          <Button
            variant="brand"
            size="sm"
            onClick={enterCompany}
            loading={impersonate.isPending}
            disabled={!primaryUser}
            title={primaryUser ? 'Открыть клиентский кабинет.' : 'У компании нет пользователя для входа.'}
          >
            Войти в кабинет компании
          </Button>
        </div>
      </div>

      <nav className={styles.tabs} aria-label="Разделы компании">
        {TABS.map((item) => (
          <button
            key={item.value}
            type="button"
            className={`${styles.tab} ${tab === item.value ? styles.tabActive : ''}`}
            onClick={() => onTab(item.value)}
            aria-current={tab === item.value ? 'page' : undefined}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {tab === 'overview' && <CompanyOverview data={data} />}
      {tab === 'minutes' && <CompanyMinutesTab companyId={companyId} />}
      {tab === 'calls' && <CompanyCallsTab companyId={companyId} />}
      {tab === 'campaigns' && <CompanyCampaignsTab campaigns={data.campaigns} />}
      {tab === 'users' && (
        <CompanyUsersTab companyName={data.company.name} users={data.users} />
      )}
      {tab === 'voice' && (
        <CompanyVoiceTab key={data.voice.updatedAt} companyId={companyId} voice={data.voice} />
      )}
      {tab === 'audit' && <CompanyAuditTab companyId={companyId} />}
    </>
  )
}

function CompanyOverview({ data }: { data: AdminCompanyDetails }) {
  const overview = data.overview

  return (
    <>
      <div className={styles.metrics}>
        <OverviewMetric label="Звонки за 30 дней" value={formatNumber(overview.calls30d)} />
        <OverviewMetric label="Минуты за 30 дней" value={formatDecimal(overview.minutes30d)} />
        <OverviewMetric
          label="Синтез за 30 дней"
          value={
            <ElevenLabsCost
              credits={overview.ttsCredits30d}
              usd={overview.ttsUsd30d}
              rate={data.elevenLabsUsdPer1kCredits}
            />
          }
        />
        <OverviewMetric
          label="Всего за 30 дней"
          value={formatApproxUsd(overview.totalUsd30d)}
          hint="LLM + TTS"
        />
        <OverviewMetric label="Доля ответов" value={formatPercent(overview.answerRate)} />
        <OverviewMetric
          label="Средняя длительность"
          value={formatDurationSeconds(Math.round(overview.avgDurationSec))}
        />
        <OverviewMetric label="Ошибки звонков" value={formatNumber(overview.failedCalls)} />
        <OverviewMetric label="Контакты" value={formatNumber(overview.contacts)} />
        <OverviewMetric label="Горячие клиенты" value={formatNumber(overview.hotClients)} />
        <OverviewMetric label="Запущенные обзвоны" value={formatNumber(overview.campaignsRunning)} />
      </div>

      <div className={styles.twoColumns}>
        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>Компания</h2>
          <dl className={styles.definitionList}>
            <InfoRow label="ID" value={String(data.company.id)} mono />
            <InfoRow label="Название" value={data.company.name} />
            <InfoRow label="Часовой пояс" value={data.company.timezone || 'Не задан'} />
            <InfoRow label="Номер для звонков" value={data.company.callerNumber || 'Не задан'} mono />
            <InfoRow label="Создана" value={formatAdminDate(data.company.createdAt)} />
            <InfoRow label="Заблокирована" value={formatAdminDate(data.company.blockedAt)} />
          </dl>
        </section>
        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>Активность</h2>
          <dl className={styles.definitionList}>
            <InfoRow label="Владелец" value={data.ownerEmail} />
            <InfoRow label="Последний звонок" value={formatAdminDate(overview.lastCallAt)} />
            <InfoRow label="Пользователи" value={formatNumber(data.users.length)} />
            <InfoRow label="Обзвоны" value={formatNumber(data.campaigns.length)} />
            <InfoRow label="Модель синтеза" value={data.voice.ttsModel || 'По умолчанию'} mono />
            <InfoRow label="Диалоговая модель" value={data.voice.llmModel || 'По умолчанию'} mono />
          </dl>
        </section>
      </div>
    </>
  )
}

function OverviewMetric({
  label,
  value,
  hint,
}: {
  label: string
  value: ReactNode
  hint?: string
}) {
  return (
    <article className={styles.metric}>
      <span className={styles.metricLabel}>{label}</span>
      <strong className={styles.metricValue} data-admin-value>
        {value}
      </strong>
      {hint && <span className={styles.metricHint}>{hint}</span>}
    </article>
  )
}

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className={styles.definitionRow}>
      <dt>{label}</dt>
      <dd className={mono ? styles.mono : undefined}>{value}</dd>
    </div>
  )
}
